// SPDX-License-Identifier: AGPL-3.0-or-later

import {marketingUrl} from '@app/features/messaging/utils/MessagingUrlUtils';

export const Routes = {
	HOME: '/',
	LOGIN: '/login',
	REGISTER: '/register',
	FORGOT_PASSWORD: '/forgot',
	RESET_PASSWORD: '/reset',
	VERIFY_EMAIL: '/verify',
	AUTHORIZE_IP: '/authorize-ip',
	EMAIL_REVERT: '/wasntme',
	PENDING: '/pending',
	OAUTH_AUTHORIZE: '/oauth2/authorize',
	SSO_CALLBACK: '/auth/sso/callback',
	INVITE_REGISTER: '/invite/:code',
	INVITE_LOGIN: '/invite/:code/login',
	THEME_REGISTER: '/theme/:themeId',
	THEME_LOGIN: '/theme/:themeId/login',
	ME: '/channels/@me',
	FAVORITES: '/channels/@favorites',
	DISCOVER: '/channels/@discover',
	BOOKMARKS: '/bookmarks',
	MENTIONS: '/mentions',
	NOTIFICATIONS: '/notifications',
	YOU: '/you',
	CALENDAR: '/calendar',
	TODAY: '/today',
	RAIL: '/rail',
	EMAILS: '/emails',
	WHATSAPP: '/whatsapp',
	TASKS: '/tasks',
	// KONVO: coding agents on your own machines (docs/konvo-agents/DESIGN.md).
	AGENTS: '/agents',
	// KONVO: everything coming up (reminders, scheduled sends, automations, digests, promises).
	SCHEDULED: '/scheduled',
	// KONVO: staff-only Team insights.
	INSIGHTS: '/insights',
	// KONVO: connected sources opened inside the Chats layout, beside the chat
	// list, instead of on their own pages.
	DM_WHATSAPP: '/channels/@me/whatsapp',
	DM_MAIL: '/channels/@me/mail',
	DM_CLICKUP: '/channels/@me/clickup',
	REPORT: '/report',
	AGE_VERIFICATION_CALLBACK: '/age-verification-callback',
	CONNECTION_CALLBACK: '/connection-callback',
	USER_PROFILE: '/users/:userId',
	THEME_STUDIO: '/theme-studio',
	terms: () => marketingUrl('terms'),
	privacy: () => marketingUrl('privacy'),
	guidelines: () => marketingUrl('guidelines'),
	careers: () => marketingUrl('careers'),
	partners: () => marketingUrl('partners'),
	bugs: () => marketingUrl('help/report-bug'),
	help: () => marketingUrl('help'),
	helpArticle: (slug: string) => marketingUrl(`help/${slug}`),
	dmChannel: (channelId: string) => `/channels/@me/${channelId}`,
	dmWhatsAppChat: (jid: string) => `${Routes.DM_WHATSAPP}/${encodeURIComponent(jid)}`,
	dmMail: (messageId?: string | null) =>
		messageId ? `${Routes.DM_MAIL}/${encodeURIComponent(messageId)}` : Routes.DM_MAIL,
	favoritesChannel: (channelId: string) => `/channels/@favorites/${channelId}`,
	guildMembers: (guildId: string) => `/channels/${guildId}/members`,
	guildChannel: (guildId: string, channelId?: string) =>
		channelId ? `/channels/${guildId}/${channelId}` : `/channels/${guildId}`,
	channelMessage: (guildId: string, channelId: string, messageId: string) =>
		`${Routes.guildChannel(guildId, channelId)}/${messageId}`,
	dmChannelMessage: (channelId: string, messageId: string) => `${Routes.dmChannel(channelId)}/${messageId}`,
	favoritesChannelMessage: (channelId: string, messageId: string) =>
		`${Routes.favoritesChannel(channelId)}/${messageId}`,
	inviteRegister: (code: string) => `/invite/${code}`,
	inviteLogin: (code: string) => `/invite/${code}/login`,
	theme: (themeId: string) => `/theme/${themeId}`,
	themeRegister: (themeId: string) => `/theme/${themeId}`,
	themeLogin: (themeId: string) => `/theme/${themeId}/login`,
	userProfile: (userId: string) => `/users/${userId}`,
	isSpecialPage: (pathname: string) =>
		pathname === Routes.BOOKMARKS ||
		pathname === Routes.MENTIONS ||
		pathname === Routes.NOTIFICATIONS ||
		pathname === Routes.YOU,
	isDMRoute: (pathname: string) => pathname.startsWith('/channels/@me'),
	isDMWhatsAppRoute: (pathname: string) => pathname.startsWith(`${Routes.DM_WHATSAPP}/`),
	isDMMailRoute: (pathname: string) => pathname === Routes.DM_MAIL || pathname.startsWith(`${Routes.DM_MAIL}/`),
	isDMClickUpRoute: (pathname: string) => pathname === Routes.DM_CLICKUP,
	/** The WhatsApp jid in a /channels/@me/whatsapp/:jid path, or null. */
	dmWhatsAppJid: (pathname: string): string | null => {
		if (!Routes.isDMWhatsAppRoute(pathname)) return null;
		const raw = pathname.slice(Routes.DM_WHATSAPP.length + 1).split('/')[0] ?? '';
		try {
			return raw ? decodeURIComponent(raw) : null;
		} catch {
			return raw || null;
		}
	},
	/** The mail open beside the Chats list, from /channels/@me/mail/:messageId. */
	dmMailId: (pathname: string): string | null => {
		if (!pathname.startsWith(`${Routes.DM_MAIL}/`)) return null;
		const raw = pathname.slice(Routes.DM_MAIL.length + 1).split('/')[0] ?? '';
		try {
			return raw ? decodeURIComponent(raw) : null;
		} catch {
			return raw || null;
		}
	},
	isCalendarRoute: (pathname: string) => pathname.startsWith('/calendar'),
	isEmailsRoute: (pathname: string) => pathname.startsWith('/emails'),
	isWhatsAppRoute: (pathname: string) => pathname.startsWith('/whatsapp'),
	isTodayRoute: (pathname: string) => pathname.startsWith('/today'),
	isTasksRoute: (pathname: string) => pathname.startsWith('/tasks'),
	isAgentsRoute: (pathname: string) => pathname === '/agents' || pathname.startsWith('/agents/'),
	/** /agents, /agents/:machineId or /agents/:machineId/:threadId */
	agents: (machineId?: string, threadId?: string) =>
		machineId
			? `/agents/${encodeURIComponent(machineId)}${threadId ? `/${encodeURIComponent(threadId)}` : ''}`
			: '/agents',
	/** /agents/machines/:machineId: how a machine is doing. */
	agentsMachine: (machineId: string) => `/agents/machines/${encodeURIComponent(machineId)}`,
	/** /agents/pulls, or /agents/pulls/:owner/:repo/:number: the Pull requests list, or one pull request's page. */
	agentsPulls: (pull?: {repository: string; number: number} | null) =>
		pull
			? `/agents/pulls/${pull.repository
					.split('/')
					.map((part) => encodeURIComponent(part))
					.join('/')}/${pull.number}`
			: '/agents/pulls',
	/** /agents/runs, or /agents/runs/:owner/:repo/:runId: GitHub Actions runs, or one run's page. */
	agentsRuns: (run?: {repository: string; runId: number} | null) =>
		run
			? `/agents/runs/${run.repository
					.split('/')
					.map((part) => encodeURIComponent(part))
					.join('/')}/${run.runId}`
			: '/agents/runs',
	/** /agents/new?machine=…&project=…: a new agent chat (both optional; the page picks defaults). */
	agentsNew: (machineId?: string | null, projectId?: string | null) => {
		const query = new URLSearchParams();
		if (machineId) query.set('machine', machineId);
		if (projectId) query.set('project', projectId);
		const search = query.toString();
		return search ? `/agents/new?${search}` : '/agents/new';
	},
	isScheduledRoute: (pathname: string) => pathname === '/scheduled' || pathname.startsWith('/scheduled/'),
	/** /scheduled?filter=…&item=…: the Scheduled page, optionally filtered and with one item open. */
	scheduled: (query: {filter?: string | null; item?: string | null} = {}) => {
		const search = new URLSearchParams();
		if (query.filter) search.set('filter', query.filter);
		if (query.item) search.set('item', query.item);
		const value = search.toString();
		return value ? `/scheduled?${value}` : '/scheduled';
	},
	isInsightsRoute: (pathname: string) => pathname === '/insights' || pathname.startsWith('/insights/'),
	/** /insights/people, /insights/schedule, /insights/suggestions; the overview is /insights. */
	insightsTab: (tab: string) => (tab === 'overview' ? '/insights' : `/insights/${tab}`),
	insightsPerson: (userId: string) => `/insights/people/${encodeURIComponent(userId)}`,
	isFavoritesRoute: (pathname: string) => pathname.startsWith('/channels/@favorites'),
	isDiscoverRoute: (pathname: string) => pathname.startsWith('/channels/@discover'),
	isChannelRoute: (pathname: string) => pathname.startsWith('/channels/'),
	isGuildChannelRoute: (pathname: string) =>
		pathname.startsWith('/channels/') &&
		!pathname.startsWith('/channels/@me') &&
		!pathname.startsWith('/channels/@favorites') &&
		!pathname.startsWith('/channels/@discover'),
} as const;
